/**
 * Screen Sharing Hook
 *
 * Manages screen sharing state and operations using LiveKit
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useRoomContext, useLocalParticipant, useRemoteParticipants } from '@livekit/components-react';
import { Track } from 'livekit-client';

export interface ScreenShareState {
  isSharing: boolean;
  isSharingAudio: boolean;
  error: string | null;
  isLoading: boolean;
}

export interface RemoteScreenShare {
  participantId: string;
  participantName: string;
  trackSid: string;
  hasAudio: boolean;
}

export function useScreenShare() {
  const room = useRoomContext();
  const { localParticipant } = useLocalParticipant();
  const remoteParticipants = useRemoteParticipants();

  const [state, setState] = useState<ScreenShareState>({
    isSharing: false,
    isSharingAudio: false,
    error: null,
    isLoading: false,
  });


  // Track local screen sharing state
  useEffect(() => {
    if (!localParticipant) return;

    const updateLocalState = () => {
      const screenTrack = Array.from(localParticipant.videoTrackPublications.values())
        .find((pub) => pub.source === Track.Source.ScreenShare);

      const screenAudioTrack = Array.from(localParticipant.audioTrackPublications.values())
        .find((pub) => pub.source === Track.Source.ScreenShareAudio);

      setState(prev => ({
        ...prev,
        isSharing: !!screenTrack?.track,
        isSharingAudio: !!screenAudioTrack?.track,
      }));
    };

    updateLocalState();

    // Listen for track changes
    localParticipant.on('trackPublished', updateLocalState);
    localParticipant.on('trackUnpublished', updateLocalState);

    return () => {
      localParticipant.off('trackPublished', updateLocalState);
      localParticipant.off('trackUnpublished', updateLocalState);
    };
  }, [localParticipant]);

  // Track remote screen shares (derived from remote participants)
  const remoteScreenShares = useMemo<RemoteScreenShare[]>(() => {
    const shares: RemoteScreenShare[] = [];

    remoteParticipants.forEach((participant) => {
      const screenTrack = Array.from(participant.videoTrackPublications.values())
        .find((pub) => pub.source === Track.Source.ScreenShare);

      if (screenTrack?.track) {
        const screenAudioTrack = Array.from(participant.audioTrackPublications.values())
          .find((pub) => pub.source === Track.Source.ScreenShareAudio);

        shares.push({
          participantId: participant.identity,
          participantName: participant.name || participant.identity,
          trackSid: screenTrack.trackSid,
          hasAudio: !!screenAudioTrack?.track,
        });
      }
    });

    return shares;
  }, [remoteParticipants]);

  /**
   * Start screen sharing
   * @param options - Screen share options
   */
  const startScreenShare = useCallback(async (options?: {
    audio?: boolean;
    preferCurrentTab?: boolean;
  }) => {
    if (!room || !localParticipant) {
      setState(prev => ({ ...prev, error: 'Не сте свързани към стая' }));
      return;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      await localParticipant.setScreenShareEnabled(true, {
        audio: options?.audio ?? true,
        preferCurrentTab: options?.preferCurrentTab ?? false,
      });

      setState(prev => ({
        ...prev,
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      console.error('[ScreenShare] Failed to start:', error);

      const err = error as { name?: string; message?: string };
      let errorMessage = 'Грешка при споделяне на екрана';

      if (err.name === 'NotAllowedError') {
        errorMessage = 'Достъпът до споделяне на екрана беше отказан';
      } else if (err.name === 'NotFoundError') {
        errorMessage = 'Не бяха намерени екрани за споделяне';
      } else if (err.name === 'AbortError') {
        errorMessage = 'Споделянето на екрана беше отменено';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
        isSharing: false,
        isSharingAudio: false,
      }));
    }
  }, [room, localParticipant]);

  /**
   * Stop screen sharing
   */
  const stopScreenShare = useCallback(async () => {
    if (!localParticipant) return;

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      await localParticipant.setScreenShareEnabled(false);

      setState(prev => ({
        ...prev,
        isLoading: false,
        isSharing: false,
        isSharingAudio: false,
        error: null,
      }));
    } catch (error) {
      console.error('[ScreenShare] Failed to stop:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Грешка при спиране на споделянето',
      }));
    }
  }, [localParticipant]);

  /**
   * Toggle screen sharing
   */
  const toggleScreenShare = useCallback(async (options?: {
    audio?: boolean;
    preferCurrentTab?: boolean;
  }) => {
    if (state.isSharing) {
      await stopScreenShare();
    } else {
      await startScreenShare(options);
    }
  }, [state.isSharing, startScreenShare, stopScreenShare]);

  /**
   * Clear error message
   */
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  return {
    // Local state
    isSharing: state.isSharing,
    isSharingAudio: state.isSharingAudio,
    error: state.error,
    isLoading: state.isLoading,

    // Remote screen shares
    remoteScreenShares,
    hasRemoteScreenShares: remoteScreenShares.length > 0,

    // Actions
    startScreenShare,
    stopScreenShare,
    toggleScreenShare,
    clearError,
  };
}
